import { useContext, useEffect, useState } from 'react';
import { MyContext } from '../../context';
import DataTable from '../../components/tables/DataTable';
import StatCard from '../../components/StatCard';
import StatusBadge from '../../components/StatusBadge';
import ExportButton from '../../components/ExportButton';
import Pagination from '../../components/Pagination';
import DateRangePicker from '../../components/DateRangePicker';
import { getWebhookDeliveriesReport, retryWebhookDelivery } from '../../utils/queries';

const PAGE_SIZE = 20;

export default function WebhookDeliveriesReport() {
	const { activeStoreId } = useContext(MyContext);
	const [range, setRange] = useState({});
	const [status, setStatus] = useState('');
	const [page, setPage] = useState(0);
	const [report, setReport] = useState(null);
	const [loading, setLoading] = useState(true);
	const [retryingId, setRetryingId] = useState(null);

	const refresh = () => {
		setLoading(true);
		return getWebhookDeliveriesReport({
			storeId: activeStoreId || undefined,
			dateFrom: range.from || undefined,
			dateTo: range.to || undefined,
			status: status || undefined,
			limit: PAGE_SIZE,
			offset: page * PAGE_SIZE,
		}).then((result) => {
			if (result?.success) setReport(result.data);
			setLoading(false);
		});
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeStoreId, range.from, range.to, status, page]);

	const filterFrom = (setter) => (e) => {
		setter(e.target.value);
		setPage(0);
	};

	const handleRetry = async (id) => {
		setRetryingId(id);
		await retryWebhookDelivery(id);
		setRetryingId(null);
		refresh();
	};

	const columns = [
		{ key: 'event_type', header: 'Event Type' },
		{ key: 'destination_url', header: 'Destination URL' },
		{
			key: 'status',
			header: 'Status',
			render: (value) => <StatusBadge status={value} text={value} />,
		},
		{ key: 'attempt_number', header: 'Attempt' },
		{ key: 'http_status_code', header: 'HTTP Status' },
		{ key: 'error_message', header: 'Error' },
		{
			key: 'last_attempt_at',
			header: 'Last Attempt',
			render: (value) => (value ? new Date(value).toLocaleString() : ''),
		},
		{
			key: 'webhook_delivery_id',
			header: '',
			sortable: false,
			render: (id, row) =>
				row.status === 'failed' ? (
					<button
						type='button'
						className='btn-secondary-small'
						disabled={retryingId === id}
						onClick={() => handleRetry(id)}
					>
						{retryingId === id ? 'Retrying...' : 'Retry'}
					</button>
				) : null,
		},
	];

	// Excludes the trailing Retry-button column -- not meaningful in an export.
	const exportColumns = columns.slice(0, -1);

	return (
		<div className='py-8 pr-8'>
			<div className='flex items-center justify-between mb-6'>
				<h1 className='text-2xl font-light tracking-tight'>Webhook Deliveries Report</h1>
				<ExportButton
					fileName='webhook-deliveries-report'
					sections={[
						{
							data: report?.rows || [],
							columns: exportColumns.map((c) => c.header),
							columnKeys: exportColumns.map((c) => c.key),
							columnTypes: ['text', 'text', 'text', 'number', 'text', 'text', 'date'],
						},
					]}
				/>
			</div>

			<div className='flex gap-4 mb-6'>
				<div>
					<label>Date Range</label>
					<DateRangePicker
						value={range}
						onChange={(item) => {
							setRange(item);
							setPage(0);
						}}
						className='w-72'
					/>
				</div>
				<div>
					<label>Status</label>
					<select className='form-select' value={status} onChange={filterFrom(setStatus)}>
						<option value=''>All</option>
						<option value='success'>Successful</option>
						<option value='failed'>Failed</option>
						<option value='pending'>Pending</option>
					</select>
				</div>
			</div>

			{loading || !report ? (
				<div className='text-sm text-gray-400'>Loading...</div>
			) : (
				<>
					<div className='grid grid-cols-5 gap-4 mb-8'>
						<StatCard label='Total Deliveries' value={report.summary.total} />
						<StatCard label='Successful' value={report.summary.succeeded} />
						<StatCard label='Failed' value={report.summary.failed} />
						<StatCard label='Pending' value={report.summary.pending} />
						<StatCard
							label='Success Rate'
							value={report.summary.success_rate != null ? `${report.summary.success_rate}%` : '-'}
						/>
					</div>

					<DataTable columns={columns} data={report.rows} loading={loading} emptyHeading='No webhook deliveries in range' />
					<Pagination
						pageCount={Math.ceil((report.pagination?.total || 0) / PAGE_SIZE)}
						currentPage={page}
						onPageChange={setPage}
					/>
				</>
			)}
		</div>
	);
}
